import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Bone, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface ChiropracticAdjustmentCardProps {
  records: any[];
  onRecordsChange: (updatedRecords: any[]) => void;
}

export function ChiropracticAdjustmentCard({ records, onRecordsChange }: ChiropracticAdjustmentCardProps) {
  const chiroRecords = records.filter(r => r.type === 'chiropractic_adjustment');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  if (chiroRecords.length === 0) return null;

  const handleEdit = (idx: number) => {
    const originalIndex = records.indexOf(chiroRecords[idx]);
    setEditForm(records[originalIndex]);
    setEditingIndex(originalIndex);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const updated = [...records];
    updated[editingIndex] = editForm;
    onRecordsChange(updated);
    setEditingIndex(null);
    toast.success("Registro atualizado!");
  };

  const handleDelete = (idx: number) => {
    const originalIndex = records.indexOf(chiroRecords[idx]);
    setDeleteIndex(originalIndex);
  };

  const confirmDelete = () => {
    if (deleteIndex === null) return;
    const updated = records.filter((_, i) => i !== deleteIndex);
    onRecordsChange(updated);
    setDeleteIndex(null);
    toast.success("Registro excluído!");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Bone className="h-5 w-5 text-indigo-600" />
        <h3 className="text-lg font-semibold text-indigo-600">Ajuste Quiroprático</h3>
        <Badge variant="secondary">{chiroRecords.length} registros</Badge>
      </div>

      <div className="space-y-3">
        {chiroRecords.map((record, idx) => (
          <Card key={idx} className="border-l-4 border-l-indigo-600">
            <CardContent className="pt-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-foreground">{record.region}</span>
                  <div className="flex items-center gap-2">
                    {record.date && (
                      <span className="text-xs text-muted-foreground">
                        {new Date(record.date).toLocaleDateString('pt-BR')}
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(idx)}
                      className="text-primary hover:bg-primary/10"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(idx)}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {record.adjustment_type && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Tipo:</span>
                    <Badge variant="secondary">{record.adjustment_type}</Badge>
                  </div>
                )}

                {record.restriction && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Restrição Identificada</p>
                    <p className="text-sm text-foreground">{record.restriction}</p>
                  </div>
                )}

                {record.patient_response && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded p-2">
                    <p className="text-xs text-indigo-700">
                      ✅ <span className="font-medium">Resposta:</span> {record.patient_response}
                    </p>
                  </div>
                )}

                {record.instructions && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2">
                    <p className="text-xs text-amber-700">
                      💡 <span className="font-medium">Orientações:</span> {record.instructions}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={editingIndex !== null} onOpenChange={() => setEditingIndex(null)}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Editar Ajuste Quiroprático</AlertDialogTitle>
          </AlertDialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Região</Label>
              <Input
                value={editForm.region || ""}
                onChange={(e) => setEditForm({ ...editForm, region: e.target.value })}
                placeholder="Ex: Coluna Cervical, Lombar"
              />
            </div>

            <div className="grid gap-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={editForm.date || ""}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Tipo de Ajuste</Label>
              <Input
                value={editForm.adjustment_type || ""}
                onChange={(e) => setEditForm({ ...editForm, adjustment_type: e.target.value })}
                placeholder="Ex: HVLA, SOT"
              />
            </div>

            <div className="grid gap-2">
              <Label>Restrição Identificada</Label>
              <Textarea
                value={editForm.restriction || ""}
                onChange={(e) => setEditForm({ ...editForm, restriction: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Resposta do Paciente</Label>
              <Textarea
                value={editForm.patient_response || ""}
                onChange={(e) => setEditForm({ ...editForm, patient_response: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Orientações</Label>
              <Textarea
                value={editForm.instructions || ""}
                onChange={(e) => setEditForm({ ...editForm, instructions: e.target.value })}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveEdit}>Salvar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteIndex !== null} onOpenChange={() => setDeleteIndex(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}